// DataSourceImportModal — confirms a CSV the user already picked, then creates a
// data source from it. The file has been parsed and validated by the time this
// opens (the page blocks on failure), so the Records card is a summary, never a
// form: nothing in it is editable and it cannot be in an error state.
//
// The three text fields are the create modal's, and required for the same
// reason — this makes the same entity, so it cannot accept less than
// DataSourceModal does. What it does NOT collect is columns: the CSV's first two
// rows carry the names and their dataTypes.
//
// Saving is three steps, because the shared api client encrypts request bodies
// and a File cannot survive that (see utils/uploadFile):
//
//   1. reserve  POST media/upload-public-file  { fileName } → { uploadUrl }
//   2. upload   PUT the raw bytes to uploadUrl, outside axios
//   3. commit   POST import/import-datasource  { …fields, fileName, filePathUrl }

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button, Field, Icon, Input, Modal } from '@/components/common';
import { useImportDataSourceMutation } from '@/api/endpoints/datasourceApi';
import { useUploadPublicFileMutation } from '@/api/endpoints/mediaApi';
import { uploadToPresignedUrl } from '@/utils/uploadFile';
import { nameFromFile, publicUrlOf, type ParsedDataSourceCsv } from '../shared/parseDataSourceCsv';

interface DataSourceImportModalProps {
  open: boolean;
  onClose: () => void;
  file: File;
  parsed: ParsedDataSourceCsv;
}

const emptyDraft = {
  schemaName: '',
  dataSourceType: '',
  externalId: '',
};

const DataSourceImportModal = ({ open, onClose, file, parsed }: DataSourceImportModalProps) => {
  const { t } = useTranslation();
  const [reserveUpload] = useUploadPublicFileMutation();
  const [importDataSource] = useImportDataSourceMutation();
  const [draft, setDraft] = useState(emptyDraft);
  const [submitted, setSubmitted] = useState(false);
  // One flag for all three steps: they are one action to the user, and the
  // mutations' own isLoading would leave the button live during the PUT, which
  // belongs to neither of them.
  const [saving, setSaving] = useState(false);

  // Re-seed on open, naming the source after the file so the common case needs
  // no typing at all.
  useEffect(() => {
    if (open) {
      setDraft({ ...emptyDraft, schemaName: nameFromFile(file.name) });
      setSubmitted(false);
    }
  }, [open, file]);

  const patch = (changes: Partial<typeof emptyDraft>) => setDraft((d) => ({ ...d, ...changes }));

  // Test the SAME trimmed value that gets submitted (see DataSourceModal).
  const schemaNameMissing = !draft.schemaName.trim();
  const dataSourceTypeMissing = !draft.dataSourceType.trim();
  const externalIdMissing = !draft.externalId.trim();

  const submit = async () => {
    setSubmitted(true);
    if (schemaNameMissing || dataSourceTypeMissing || externalIdMissing) return;
    setSaving(true);
    try {
      const { uploadUrl } = await reserveUpload({ fileName: file.name }).unwrap();
      await uploadToPresignedUrl(uploadUrl, file);
      await importDataSource({
        schemaName: draft.schemaName.trim(),
        dataSourceType: draft.dataSourceType.trim(),
        externalId: draft.externalId.trim(),
        fileName: file.name,
        filePathUrl: publicUrlOf(uploadUrl),
      }).unwrap();
      toast.success(t('datasource_import_success', { name: draft.schemaName.trim() }));
      onClose();
    } catch {
      // Left open on purpose: the typed fields survive so a retry is one click,
      // and re-uploading the file costs only the (cheap) reserve.
      toast.error(t('error_generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon='upload'
      title={t('datasource_import_title')}
      subtitle={t('datasource_import_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          <Button iconStart='upload' onClick={submit} loading={saving}>
            {t('datasource_import_submit')}
          </Button>
        </>
      }
    >
      <div className='datasource-create'>
        <Field
          label={t('datasource_field_schema_name')}
          required
          error={submitted && schemaNameMissing ? t('datasource_schema_name_required') : undefined}
        >
          <Input
            value={draft.schemaName}
            placeholder={t('datasource_field_schema_name_placeholder')}
            error={submitted && schemaNameMissing}
            onChange={(e) => patch({ schemaName: e.target.value })}
          />
        </Field>
        <div className='datasource-create__row'>
          <Field
            label={t('datasource_field_type')}
            required
            error={
              submitted && dataSourceTypeMissing ? t('datasource_field_type_required') : undefined
            }
          >
            <Input
              value={draft.dataSourceType}
              placeholder={t('datasource_field_type_placeholder')}
              error={submitted && dataSourceTypeMissing}
              onChange={(e) => patch({ dataSourceType: e.target.value })}
            />
          </Field>
          <Field
            label={t('datasource_field_external_id')}
            required
            error={
              submitted && externalIdMissing ? t('datasource_external_id_required') : undefined
            }
          >
            <Input
              value={draft.externalId}
              placeholder={t('datasource_field_external_id_placeholder')}
              error={submitted && externalIdMissing}
              onChange={(e) => patch({ externalId: e.target.value })}
            />
          </Field>
        </div>
        {/* Read-only summary of what was parsed — the counts are the user's only
            confirmation that the file was understood the way they expect. */}
        <Field label={t('datasource_import_records')}>
          <div className='datasource-import__file'>
            <span className='datasource-import__file-icon' aria-hidden='true'>
              <Icon name='file-check' size={20} />
            </span>
            <span className='datasource-import__file-text'>
              <span className='datasource-import__file-name'>{file.name}</span>
              <span className='datasource-import__file-meta'>
                {t('datasource_import_records_meta', {
                  records: parsed.recordCount,
                  columns: parsed.columnCount,
                  count: parsed.recordCount,
                })}
              </span>
            </span>
          </div>
        </Field>
      </div>
    </Modal>
  );
};

export default DataSourceImportModal;
